import React from "react";

import supabase from "@/services/supabaseClient";
import {
  groupAlertsForAttention,
  hydrateAlert,
} from "@/utils/alertModel.js";
import { isMessageNotification } from "@/utils/notificationTargets.js";

function isRecoverableNotificationsError(error) {
  const message = String(error?.message || "").toLowerCase();
  return /permission denied|row level security|rls|not allowed|unauthorized/.test(message);
}

export default function useUnreadNotifications(userId) {
  const stableUserId = String(userId || "").trim();
  const [loading, setLoading] = React.useState(true);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [unreadItems, setUnreadItems] = React.useState([]);

  const load = React.useCallback(async () => {
    if (!stableUserId) {
      setUnreadCount(0);
      setUnreadItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase
        .from("notifications")
        .select(
          "id, title, message, type, priority, source, audience, channels, quiet_by_default, escalation_eligible, group_key, read_at, created_at, updated_at"
        )
        .eq("user_id", stableUserId)
        .is("read_at", null)
        .order("updated_at", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(250);

      if (error) throw error;

      const unreadBellAlerts = groupAlertsForAttention(
        (Array.isArray(data) ? data : [])
          .filter((row) => !isMessageNotification(row))
          .map((row) => hydrateAlert(row))
          .filter((row) => row?.bellEligible)
      );

      setUnreadCount(unreadBellAlerts.length);
      setUnreadItems(unreadBellAlerts);
    } catch (error) {
      if (!isRecoverableNotificationsError(error)) {
        console.warn("Unread notifications load failed:", error);
      }
      setUnreadCount(0);
      setUnreadItems([]);
    } finally {
      setLoading(false);
    }
  }, [stableUserId]);

  React.useEffect(() => {
    load();

    const onChanged = () => load();
    const onFocus = () => load();
    const onVisibility = () => {
      if (document.visibilityState === "visible") load();
    };

    const channel = stableUserId
      ? supabase
          .channel(`notifications-${stableUserId}`)
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "notifications",
              filter: `user_id=eq.${stableUserId}`,
            },
            onChanged
          )
          .subscribe()
      : null;

    window.addEventListener("afrimed:notifications:changed", onChanged);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
      window.removeEventListener("afrimed:notifications:changed", onChanged);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);

  return {
    loading,
    unreadCount,
    unreadItems,
    refetch: load,
  };
}
